/**
 * TaskSelectionService is a service class responsible for managing the selection of task UUIDs.
 * It interacts with the route, bridges, and UserInfoService to detect and select task UUIDs.
 */
class TaskSelectionService {
    /**
     * Constructs a new TaskSelectionService instance.
     * @param {Object} $route - The AngularJS route service.
     * @param {Object} $bridges - The bridges service.
     * @param {Object} UserInfoService - The UserInfoService instance.
     */
    constructor($route, $bridges, UserInfoService) {
        this.$route = $route;
        this.$bridges = $bridges;
        this.UserInfoService = UserInfoService;
        this._logIdentifier = 'TaskSelectionService';
        this._taskUUID = null;
        this.$logger = $bridges.logger();
    }

    /**
     * Selects a task UUID and logs the action.
     * @param {string} taskUUID - The UUID of the task to be selected.
     */
    selectTaskUUID(taskUUID) {
        this.$logger.info(this._logIdentifier, "Selecting task UUID " + taskUUID);
        this._taskUUID = taskUUID;
        // Store taskUUID in sessionStorage
        sessionStorage.setItem('selectedTaskUUID', this._taskUUID);
    }

    /**
     * Attempts to detect the task UUID from the current route.
     * @returns {string|null} - The detected task UUID or null if not found.
     */
    detectTaskUUIDFromRoute() {
        this.$logger.info(this._logIdentifier, "Attempting to detect task UUID from route");
        const uuidRoute = this.$route.current.params.taskUUID;
        if (angular.isDefined(uuidRoute)) {
            this.selectTaskUUID(uuidRoute);
        }
        return this.getTaskUUID(false);
    }

    /**
     * Attempts to detect the task UUID from the user info.
     */
    detectTaskUUIDFromUserInfo() {
        this.$logger.info(this._logIdentifier, "Attempting to detect task UUID from user info");
        const userInfo = this.UserInfoService.getUserInfo();
        if (userInfo === null || !angular.isFunction(userInfo.getTaskUUID)) {
            return;
        }
        const uiTaskUUID = userInfo.getTaskUUID();
        if (angular.isDefined(uiTaskUUID) && uiTaskUUID !== null) {
            this.selectTaskUUID(uiTaskUUID);
        }
    }

    /**
     * Retrieves the currently selected task UUID.
     * Optionally attempts to detect the task UUID from the route or user info if not already selected.
     * @param {boolean} [allowAttemptDetectionFromRoute=true] - Whether to attempt detection from the route.
     * @param {boolean} [allowAttemptDetectionFromUserInfo=true] - Whether to attempt detection from user info.
     * @returns {string|null} - The currently selected task UUID or null if not found.
     */
    getTaskUUID(allowAttemptDetectionFromRoute = true, allowAttemptDetectionFromUserInfo = true) {
        if (!this.isTaskSelected()) {
            if (allowAttemptDetectionFromRoute) {
                this.detectTaskUUIDFromRoute();
            }
            if (!this.isTaskSelected() && allowAttemptDetectionFromUserInfo) {
                this.detectTaskUUIDFromUserInfo();
            }    
            if (!this.isTaskSelected()) {
                this.$logger.info(this._logIdentifier, "No attempt to detect task UUID from route");
                // Try to restore from sessionStorage
                const cachedTaskUUID = sessionStorage.getItem('selectedTaskUUID');
                if (cachedTaskUUID) {
                    this._taskUUID = cachedTaskUUID;
                    this.$logger.info(this._logIdentifier, "Restored task UUID " + this._taskUUID + " from sessionStorage");
                } 
            }
        }

        return this._taskUUID;
    }

    /**
     * Checks if a task UUID is currently selected.
     * @returns {boolean} - True if a task UUID is selected, false otherwise.
     */
    isTaskSelected() {
        return this._taskUUID !== null;
    }

    /**
     * Clears the currently selected task UUID.
     */
    clear() {
        this._taskUUID = null;
    }
}

export default TaskSelectionService;